import { Alert, Button, Card, CardContent, Snackbar } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { availableFallbackGroups, BlockedGroupModal } from "../features/groups/BlockedGroupModal";
import { ApiError, financeApi, isSupportView, stopSupportView, SUPPORT_VIEW_GROUP_KEY, SUPPORT_VIEW_READONLY_MESSAGE } from "../lib/api";
import { useDevDateOverride } from "../lib/devDate";
import { queryKeys } from "../lib/queryKeys";
import { Sidebar } from "./Sidebar";

const selectedGroupStorageKey = "financeSelectedGroupId";

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const previousPathnameRef = useRef(location.pathname);
  useDevDateOverride();
  const meQuery = useQuery({ queryKey: queryKeys.me, queryFn: financeApi.me, retry: false });
  const groupsQuery = useQuery({ queryKey: queryKeys.groups, queryFn: financeApi.groups, retry: false });
  const queryClient = useQueryClient();
  const groups = useMemo(() => groupsQuery.data || [], [groupsQuery.data]);
  const [selectedGroupId, setSelectedGroupId] = useState(() => localStorage.getItem(SUPPORT_VIEW_GROUP_KEY) || localStorage.getItem(selectedGroupStorageKey) || "");
  const [readonlySnackbarOpen, setReadonlySnackbarOpen] = useState(false);
  const supportView = isSupportView();
  const [apiBlockedGroupIds, setApiBlockedGroupIds] = useState<Set<string>>(() => new Set());
  const group = groups.find((item) => item.id === selectedGroupId) || groups[0] || null;
  const visibleGroups = supportView && group ? [group] : groups;
  const groupBlocked = Boolean(group?.is_blocked || (group && apiBlockedGroupIds.has(group.id)));
  const authRequired = [meQuery.error, groupsQuery.error].some((error) => error instanceof ApiError && error.status === 401);

  useEffect(() => {
    if (!groups.length) return;
    const supportGroupId = localStorage.getItem(SUPPORT_VIEW_GROUP_KEY);
    if (supportGroupId && groups.some((item) => item.id === supportGroupId)) {
      setSelectedGroupId(supportGroupId);
      return;
    }
    const selectedGroupExists = groups.some((item) => item.id === selectedGroupId);
    if (selectedGroupId && selectedGroupExists) return;
    setSelectedGroupId(groups[0].id);
  }, [groups, selectedGroupId]);

  useEffect(() => {
    if (supportView || !selectedGroupId) return;
    localStorage.setItem(selectedGroupStorageKey, selectedGroupId);
  }, [selectedGroupId, supportView]);

  useEffect(() => {
    const handleReadonly = () => setReadonlySnackbarOpen(true);
    window.addEventListener("finance:support-view-readonly", handleReadonly);
    return () => window.removeEventListener("finance:support-view-readonly", handleReadonly);
  }, []);

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const isHomeRecordsTransition =
      (previousPathname === "/home" && location.pathname === "/records") ||
      (previousPathname === "/records" && location.pathname === "/home");

    if (isHomeRecordsTransition) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    previousPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const markBlockedGroup = (groupId: string) => {
    setApiBlockedGroupIds((current) => {
      if (current.has(groupId)) return current;
      const next = new Set(current);
      next.add(groupId);
      return next;
    });
  };

  const selectGroup = (groupId: string) => {
    if (supportView) return;
    setSelectedGroupId(groupId);
  };

  const exitSupportView = () => {
    stopSupportView();
    queryClient.clear();
    setSelectedGroupId(localStorage.getItem(selectedGroupStorageKey) || "");
    navigate("/backoffice/users", { replace: true });
  };

  const leaveBlockedMutation = useMutation({
    mutationFn: async () => {
      if (!group || !meQuery.data) throw new Error("Group is required");
      await financeApi.deleteMember(group.id, meQuery.data.id);
    },
    onSuccess: async () => {
      if (group) {
        const fallback = availableFallbackGroups(groups, group.id)[0];
        if (fallback) setSelectedGroupId(fallback.id);
      }
      await groupsQuery.refetch();
      await queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });

  useEffect(() => {
    if (!authRequired) return;
    localStorage.removeItem("financeToken");
    stopSupportView();
  }, [authRequired]);

  if (meQuery.isLoading || groupsQuery.isLoading) {
    return (
      <section className="auth-shell">
        <Card className="auth-card">
          <CardContent>
            <h1>Aibros Finance</h1>
            <p>Loading session...</p>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (authRequired) {
    return <Navigate to="/" replace />;
  }

  if (!groupsQuery.isLoading && !groupsQuery.isError && !groups.length && meQuery.data) {
    return <Navigate to="/groups/new" replace />;
  }

  if (meQuery.isError || groupsQuery.isError || !group) {
    return (
      <section className="auth-shell">
        <Card className="auth-card">
          <CardContent>
            <h1>Aibros Finance</h1>
            <h2>Login required</h2>
            <p>Sign in to continue.</p>
            <Button className="primary-link" component={Link} to="/">Open login</Button>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <div className={supportView ? "app-shell support-view-shell" : "app-shell"}>
      {supportView ? (
        <div className="support-view-badge">
          <span>Support view</span>
          <button type="button" onClick={exitSupportView}>
            Exit
          </button>
        </div>
      ) : null}
      <Sidebar
        user={meQuery.data!}
        group={group}
        groups={visibleGroups}
        selectedGroupId={group.id}
        onSelectGroup={selectGroup}
      />
      <main className="main">
        {groupBlocked ? (
          <BlockedGroupModal
            group={group}
            groups={groups}
            isLeaving={leaveBlockedMutation.isPending}
            onLeave={() => leaveBlockedMutation.mutate()}
            onSelectGroup={selectGroup}
          />
        ) : (
          <Outlet context={{ group, user: meQuery.data!, onBlockedGroupAccess: markBlockedGroup }} />
        )}
      </main>
      <Snackbar
        open={readonlySnackbarOpen}
        autoHideDuration={4200}
        onClose={() => setReadonlySnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setReadonlySnackbarOpen(false)}>
          {SUPPORT_VIEW_READONLY_MESSAGE}
        </Alert>
      </Snackbar>
    </div>
  );
}
